/**
 * bb-addons.js
 * ───────────────────────────────────────────────────────────────────────────
 * Planting & Guarantee add-on upsell (feature spec: docs/addon-services-feature-spec.md).
 *
 * When a `guarantee`-tagged plant is added to cart AND the selected variant's SKU size token
 * matches at least one add-on in `green-glove-planting-services` / `guarantees`, we intercept
 * the add, open a modal of matched services, and write the parent + chosen add-ons to the cart
 * in ONE atomic /cart/add.js call — linked by a shared `_addon_group` line-item property so the
 * cart can nest the add-ons beneath their parent (§9). Products that aren't tagged, or have no
 * size match, are never touched: the theme's own add-to-cart runs unchanged.
 *
 * Interception strategy: the theme delegates `on:submit` / `on:click` via a *document*-level
 * capture listener (assets/component.js). A capture listener on `window` sits one step further
 * out in the propagation path, so it runs FIRST — `stopImmediatePropagation()` there preempts
 * the theme handler cleanly, no registration-order race. We only preempt for eligible+matched
 * forms; otherwise we return without touching the event.
 *
 * That "no registration-order race" is true against component.js specifically, because window
 * and document are different NODES — phase order beats load order there regardless of which
 * script tag comes first. It is NOT true against bb-cart-overlay.js's own window-capture
 * listener: that module is a static import of THIS one (see below), so its top-level
 * window.addEventListener calls run before this file's own boot(), which means it is registered
 * FIRST, not this file. stopImmediatePropagation() here therefore cannot preempt it — an earlier
 * version of this comment (and of bb-cart-overlay.js's own) claimed the opposite. The curtain
 * listener now closes that gap on its own side by recognising an eligible+matched form and
 * no-op'ing (see isAddonEligible in bb-cart-overlay.js), rather than depending on this file to
 * stop it.
 *
 * Decisions locked from the spec's decision log: D1 add-on-attach (dismiss still adds the
 * plant), D2/D3 at-most-one per category, D4 new group per add action, D5 add-on qty = parent
 * qty (1:1). Pricing is never sent from the client — only {id, quantity, properties} (§10).
 */

// Horizon 4.1.3 moved cart events out of the theme and into Shopify's own module.
// CartAddEvent, CartUpdateEvent and ThemeEvents.cartUpdate were all removed; the
// replacement is StandardEvents.cartLinesUpdate + CartLinesUpdateEvent, which
// carries a promise resolving to { cart, detail } instead of the cart directly.
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { showCartOverlay, hideCartOverlay } from '@theme/bb-cart-overlay';

const DIALOG_ID = 'bb-addons-dialog';
const DATA_SELECTOR = 'script[data-bb-addons]';
const ADD_BUTTON_SELECTOR = 'button[name="add"], [ref="addToCartButton"]';
/** Matches product-form.js's ERROR_MESSAGE_DISPLAY_DURATION so both surfaces agree. */
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
/** Timer per component, so spam-clicking re-arms rather than stacking timeouts. */
const errorTimers = new WeakMap();

/** @typedef {{id: string|number, t: string, title: string, variant: string, price: number, priceText: string, available: boolean, url: string, image: string}} AddOn */

/** Modal/add state for the in-flight add-to-cart. @type {null | {form: HTMLFormElement, component: Element, parentTitle: string, quantity: number, finalized: boolean}} */
let pending = null;
/** Guards re-entrancy while an add is in flight. */
let busy = false;
/** Guards the orphan-reconcile pass from reacting to its own cart update. */
let reconciling = false;

/**
 * Hold the cart's busy state across the WHOLE edit, theme pass + reconcile.
 *
 * Editing a tree's quantity is two sequential round-trips: the theme updates
 * that one line and re-renders, then this file's reconcile syncs the add-on
 * lines and re-renders again. component-cart-items.js only owns the first —
 * it adds .cart-items-disabled on its request and removes it in its own finally
 * — so the spinner cleared, the shopper saw the tree at its new quantity beside
 * add-ons still showing the old one, and then the whole block jumped a second
 * time. That flash of stale, internally inconsistent content is what reads as a
 * page refresh.
 *
 * .bb-loading is a SEPARATE class from the theme's .cart-items-disabled on
 * purpose. bb-design.css maps both (plus [aria-busy]) to the same spinner and
 * dim, so the two overlap seamlessly — but sharing the class would mean the
 * theme's finally clears ours mid-reconcile, which is the bug we are fixing.
 *
 * @returns {Element[]} elements marked, to hand back to clearCartBusy
 */
function markCartBusy() {
  // .cart-items, not <cart-items-component>. The custom element has no display
  // rule anywhere in the theme, so it computes to inline — and the overlay is an
  // absolutely positioned ::after with inset:0 against `position: relative`,
  // which on an inline box resolves to the text's own line boxes rather than the
  // cart. The .cart-items div (cart-products.liquid:88) is a real block, and it
  // is the shared container for both the cart page and the drawer.
  const targets = [...document.querySelectorAll('.cart-items')];
  for (const el of targets) el.classList.add('bb-loading');
  return targets;
}

/** @param {Element[]} targets */
function clearCartBusy(targets) {
  for (const el of targets) el.classList.remove('bb-loading');
}

// ── Data + matching ────────────────────────────────────────────────────────

/** @param {Element | null} component @returns {null | {parentTitle: string, variants: Record<string,string>, planting: AddOn[], guarantee: AddOn[]}} */
function readData(component) {
  const el = component?.querySelector(DATA_SELECTOR);
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.error('[bb-addons] could not parse add-on data', error);
    return null;
  }
}

/** Read the variant id the form will submit. @param {HTMLFormElement} form */
function variantIdOf(form) {
  return String(new FormData(form).get('id') || '');
}

/**
 * Read the quantity to add (defaults to 1). The PDP quantity selector lives in
 * <product-form-component> but OUTSIDE the buy-buttons <form>, so FormData(form)
 * never sees it — read it the way the theme does (product-form.js #getQuantity):
 * the quantity-selector input within the component. Falls back to the form field
 * (cart/quick contexts) then 1.
 * @param {HTMLFormElement} form
 */
function quantityOf(form) {
  const component = form.closest('product-form-component');
  const input = component?.querySelector(
    'quantity-selector-component input[ref="quantityInput"], input[name="quantity"]'
  );
  const raw = input instanceof HTMLInputElement ? input.value : new FormData(form).get('quantity');
  const q = parseInt(String(raw || ''), 10);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

/**
 * Matched add-ons for the selected variant. Tokens are compared as exact, lowercased strings
 * (so `07` ≠ `7`, per §6.2); a blank token (no dash / trailing dash / no SKU) matches nothing.
 * @param {{variants: Record<string,string>, planting: AddOn[], guarantee: AddOn[]}} data
 * @param {string} variantId
 */
function matchesFor(data, variantId) {
  const token = (data.variants && data.variants[variantId]) || '';
  if (!token) return { token: '', planting: [], guarantee: [] };
  const filter = (/** @type {AddOn[]} */ list) => (list || []).filter((a) => a.t === token);
  return { token, planting: filter(data.planting), guarantee: filter(data.guarantee) };
}

// ── Interception ───────────────────────────────────────────────────────────

/** @param {Event} event */
function onClickCapture(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(ADD_BUTTON_SELECTOR);
  if (!button || button.matches('[disabled]')) return;
  const form = button.closest('form');
  if (form) intercept(event, form);
}

/** Covers keyboard submit (Enter) where no add-button click fires. @param {Event} event */
function onSubmitCapture(event) {
  const form = event.target;
  if (form instanceof HTMLFormElement) intercept(event, form);
}

/**
 * Decide whether to take over this add-to-cart. We only preempt the theme for an eligible
 * (`guarantee`-tagged) product whose selected variant has at least one size match. Everything
 * else returns untouched so Horizon's native flow runs.
 * @param {Event} event
 * @param {HTMLFormElement} form
 */
function intercept(event, form) {
  const component = form.closest('product-form-component');
  if (!component) return;
  const data = readData(component);
  if (!data) return; // not eligible — no add-on data was rendered for this product

  if (busy) {
    // An add is already in flight; swallow the duplicate trigger.
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const match = matchesFor(data, variantIdOf(form));
  if (!match.planting.length && !match.guarantee.length) return; // no size match → native add

  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    openModal(form, component, data, match);
  } catch (error) {
    // The event is already preventDefault'ed + stopImmediatePropagation'ed above,
    // so a throw in here (e.g. the card template markup is malformed and
    // firstElementChild is missing) would otherwise make Add to cart do NOTHING —
    // same defensive fallback as the missing-dialog branch inside openModal:
    // honor the click by adding the plant alone.
    console.error('[bb-addons] could not open the add-on modal', error);
    // finalize() is async and this catch is not; without a handler its rejection
    // would escape as an unhandled rejection. It reports to the shopper itself.
    finalize([]).catch((err) => console.error('[bb-addons] fallback add failed', err));
  }
}

// ── Modal ──────────────────────────────────────────────────────────────────

/**
 * @param {HTMLFormElement} form
 * @param {Element} component
 * @param {{parentTitle: string}} data
 * @param {{planting: AddOn[], guarantee: AddOn[]}} match
 */
function openModal(form, component, data, match) {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  const quantity = quantityOf(form);
  pending = { form, component, parentTitle: data.parentTitle || '', quantity, finalized: false };

  // Defensive: if the modal markup is missing, honor the click by adding the plant alone.
  if (!dialog || typeof dialog.showModal !== 'function') {
    finalize([]);
    return;
  }

  // Guarantee before planting, matching the cart/checkout line order the payload
  // is sorted into (see addonRank).
  //
  // NOTE: this call order does NOT control what the shopper sees. renderSection
  // fills sections that already exist in bb-addons-modal.liquid, so the visible
  // order is that snippet's markup order — which is why an earlier version of
  // this comment claimed the surfaces matched while the modal still led with
  // planting. The markup is now guarantee-first to match; changing the order
  // means editing addonRank AND that snippet together.
  renderSection(dialog, 'guarantee', match.guarantee, quantity);
  renderSection(dialog, 'planting', match.planting, quantity);
  updateTotal(dialog);
  updateConfirmLabel(dialog);
  dialog.showModal();
  document.documentElement.classList.add('bb-modal-open'); // body scroll-lock [R-10]
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {'planting'|'guarantee'} role
 * @param {AddOn[]} items
 * @param {number} quantity - parent quantity; each add-on is added 1:1 (D5)
 */
function renderSection(dialog, role, items, quantity) {
  const section = dialog.querySelector(`[data-bb-addons-section="${role}"]`);
  const list = dialog.querySelector(`[data-bb-addons-list="${role}"]`);
  const template = /** @type {HTMLTemplateElement | null} */ (dialog.querySelector('#bb-addon-card-template'));
  if (!(section instanceof HTMLElement) || !list || !template) return;

  list.textContent = '';
  if (!items.length) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  // Opt-out default: preselect the first available add-on in this section, so both
  // categories (planting + guarantee) start selected and the customer clicks to
  // deselect. Single-select per section (D3), so at most one is preselected here.
  let preselected = false;
  for (const item of items) {
    const node = /** @type {HTMLElement | null} */ (template.content.firstElementChild?.cloneNode(true));
    // The `?.` above means a missing template child produces `null`, not a
    // throw — so it is handled here, honestly, rather than silently relocated
    // to a worse "cannot read properties of null" one line down inside
    // querySelector. Skipping this card still leaves openModal's own try/catch
    // (see intercept) as the backstop for anything else malformed.
    if (!node) continue;
    const card = /** @type {HTMLButtonElement} */ (node.querySelector('[data-bb-addon-card]'));
    card.dataset.id = String(item.id);
    card.dataset.role = role;
    card.dataset.price = String(item.price || 0); // numeric (cents) for the live total
    card.setAttribute('aria-pressed', 'false');

    const img = node.querySelector('[data-bb-card-image]');
    if (img instanceof HTMLImageElement) {
      if (item.image) {
        img.src = item.image;
        img.alt = item.title || '';
      } else {
        img.closest('.bb-addon-card__media')?.remove();
      }
    }
    setText(node, '[data-bb-card-title]', item.title || '');
    setText(node, '[data-bb-card-variant]', item.variant && item.variant !== 'Default Title' ? item.variant : '');
    // Reflect the parent quantity in the price (unit × qty) so a multi-qty order is clear.
    let priceText = '';
    if (item.available === false) {
      priceText = 'Sold out';
    } else if (item.priceText) {
      priceText = quantity > 1 ? `${item.priceText} × ${quantity}` : item.priceText;
    }
    setText(node, '[data-bb-card-price]', priceText);

    // Out-of-stock add-on: shown but not selectable (§8 — show disabled with a label).
    if (item.available === false) {
      card.disabled = true;
      card.classList.add('bb-addon-card--unavailable');
    } else if (!preselected) {
      // Default-on: first available option in this section starts selected.
      card.setAttribute('aria-pressed', 'true');
      preselected = true;
    }
    fillDetails(dialog, node, /** @type {'planting'|'guarantee'} */ (role));
    list.appendChild(node);
  }
}

/**
 * Per-role accordion copy, translated in bb-addons-modal.liquid and read once.
 * Falls back to null so a missing/!valid island simply hides the accordion
 * rather than throwing mid-render.
 */
let copyCache;
function addonCopy(dialog) {
  if (copyCache !== undefined) return copyCache;
  const el = dialog.querySelector('[data-bb-addons-copy]');
  try {
    copyCache = el?.textContent ? JSON.parse(el.textContent) : null;
  } catch (error) {
    console.warn('[bb-addons] could not parse accordion copy', error);
    copyCache = null;
  }
  return copyCache;
}

/**
 * Fill one card's <details>. Collapsed by default (no `open` attribute), and a
 * sibling of the card button — never a child, which would be invalid HTML.
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement} node - the cloned <li>
 * @param {'planting'|'guarantee'} role
 */
function fillDetails(dialog, node, role) {
  const details = node.querySelector('[data-bb-addon-details]');
  if (!(details instanceof HTMLElement)) return;

  const copy = addonCopy(dialog);
  const roleCopy = copy?.[role];
  if (!roleCopy) {
    details.remove(); // nothing to disclose — don't render an empty accordion
    return;
  }

  setText(node, '[data-bb-details-label]', copy.detailsLabel || '');
  setText(node, '[data-bb-details-blurb]', roleCopy.blurb || '');

  const list = node.querySelector('[data-bb-details-benefits]');
  const template = /** @type {HTMLTemplateElement | null} */ (
    dialog.querySelector('#bb-addon-benefit-template')
  );
  if (!list || !template) return;
  list.textContent = '';
  for (const benefit of roleCopy.benefits || []) {
    const row = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    setText(row, '[data-bb-benefit-text]', benefit);
    list.appendChild(row);
  }
}

/**
 * The confirm button reads "Add to cart" while an add-on is selected. Once the
 * customer has deselected every one, it names what will actually be added and
 * agrees with the tree quantity: "Add tree to cart" / "Add trees to cart".
 *
 * Both plural forms are rendered by Liquid (the quantity is only known at click
 * time), so this picks between them rather than assembling the wording.
 * @param {HTMLDialogElement} dialog
 */
function updateConfirmLabel(dialog) {
  const button = dialog.querySelector('[data-bb-addons-confirm]');
  if (!(button instanceof HTMLElement)) return;
  if (!button.dataset.labelWithAddons) {
    button.dataset.labelWithAddons = button.textContent?.trim() || '';
  }

  const none = dialog.querySelectorAll('[data-bb-addon-card][aria-pressed="true"]').length === 0;
  if (!none) {
    button.textContent = button.dataset.labelWithAddons;
    return;
  }

  const forms = addonCopy(dialog)?.confirmNoAddons;
  if (!forms) return;
  // The quantity being added, captured when the modal opened. Defaults to 1 so a
  // missing value reads as the singular rather than the wrong plural.
  const quantity = Number(pending?.quantity) || 1;
  const next = quantity === 1 ? forms.one : forms.other;
  if (next) button.textContent = next;
}

/** @param {HTMLDialogElement} dialog */
function onDialogClick(dialog, event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const card = target.closest('[data-bb-addon-card]');
  if (card instanceof HTMLButtonElement) {
    if (card.disabled) return;
    toggleCard(dialog, card);
    updateTotal(dialog);
    updateConfirmLabel(dialog);
    return;
  }
  if (target.closest('[data-bb-addons-confirm]')) {
    finalizeFromDialog(dialog, collectSelected(dialog));
    return;
  }
  if (target.closest('[data-bb-addons-skip]') || target.closest('[data-bb-addons-close]')) {
    finalizeFromDialog(dialog, []);
  }
}

/** Single-select per category (D3): selecting one card clears its siblings. */
function toggleCard(dialog, card) {
  const wasPressed = card.getAttribute('aria-pressed') === 'true';
  for (const sibling of dialog.querySelectorAll(`[data-bb-addon-card][data-role="${card.dataset.role}"]`)) {
    sibling.setAttribute('aria-pressed', 'false');
  }
  card.setAttribute('aria-pressed', wasPressed ? 'false' : 'true');
}

/** @returns {{id: string, role: string}[]} */
function collectSelected(dialog) {
  return [...dialog.querySelectorAll('[data-bb-addon-card][aria-pressed="true"]')].map((card) => ({
    id: /** @type {HTMLElement} */ (card).dataset.id || '',
    role: /** @type {HTMLElement} */ (card).dataset.role || '',
  }));
}

function finalizeFromDialog(dialog, selected) {
  if (pending) pending.finalized = true;
  dialog.close();
  finalize(selected);
}

// ── Writing to cart ──────────────────────────────────────────────────────────

/** Per add action, unique enough for grouping (D4). */
function makeGroupId() {
  return `bb-${Date.now().toString(36)}-${Math.floor(performance.now()).toString(36)}`;
}

/**
 * Sort rank for an add-on role. Lower sorts earlier, directly after the parent.
 * Unknown roles fall to the end rather than being dropped, so a future service
 * category still lands in the cart even before this list is updated.
 * @param {string} role
 * @returns {number}
 */
function addonRank(role) {
  const order = ['guarantee', 'planting'];
  const index = order.indexOf(role);
  return index === -1 ? order.length : index;
}

/**
 * Build the atomic add payload and write it. Add-ons inherit the parent quantity 1:1 (D5).
 * No price is ever sent — Shopify prices each line from its own variant (§10).
 * @param {{id: string, role: string}[]} selected
 */
async function finalize(selected) {
  if (!pending || busy) return;

  const { form, component, parentTitle } = pending;
  const parentId = variantIdOf(form);
  const quantity = quantityOf(form);

  /** @type {Array<{id: string, quantity: number, properties?: Record<string,string>}>} */
  /**
   * Lines in the order they should READ in the cart and at checkout:
   * parent, then guarantee, then planting. Built this way round deliberately —
   * this array is the intent, and the send order is derived from it below.
   * @type {Array<{id: string, quantity: number, properties?: Record<string,string>}>}
   */
  const displayOrder = [];
  if (selected.length) {
    const groupId = makeGroupId();
    displayOrder.push({ id: parentId, quantity, properties: { _addon_group: groupId, _addon_role: 'parent' } });
    // Sorted by role rather than by the modal's DOM order, so the sequence does
    // not depend on which section happens to render first.
    for (const addon of [...selected].sort((a, b) => addonRank(a.role) - addonRank(b.role))) {
      displayOrder.push({
        id: addon.id,
        quantity,
        properties: { _addon_group: groupId, _addon_role: addon.role, _addon_parent: parentTitle },
      });
    }
  } else {
    // Dismissed / nothing chosen — just the plant, no group needed (D1).
    displayOrder.push({ id: parentId, quantity });
  }

  /**
   * DO NOT "tidy" this reverse away.
   *
   * Shopify returns a multi-item /cart/add.js in the REVERSE of the submitted
   * order. Verified empirically rather than assumed: an empty cart plus one add
   * carrying both services listed the services ABOVE the tree at checkout, with
   * the payload in parent-first order.
   *
   * The cart PAGE masked this — cart-products.liquid:247 re-sequences rows with
   * CSS `order` — so it only ever surfaced at checkout, which renders raw cart
   * line order and is the thing the customer actually reads.
   *
   * So we send planting → guarantee → parent, and it comes back parent →
   * guarantee → planting. If Shopify ever changes that behaviour the symptom is
   * the order inverting again, and the fix is to delete this one line, not to
   * re-sort displayOrder above.
   */
  const items = [...displayOrder].reverse();

  // The modal has just closed and the bundle POST is about to start. Raise the
  // curtain now rather than when the cart event fires: that is two round trips
  // away (add, then cart re-read), and the whole point is to hold the page still
  // for exactly that window.
  showCartOverlay();

  // Claimed HERE, immediately before the try, not at the top of the function.
  // Everything above is synchronous — no await between the `busy` guard and this
  // line — so re-entry is still impossible, but a throw while building the
  // payload (variantIdOf/quantityOf/showCartOverlay) can no longer strand
  // `busy = true` with no finally to clear it. That matters more than it used to:
  // onRemoveClickCapture now SWALLOWS Remove clicks while busy, so a stranded
  // flag would leave Remove permanently dead and the cart permanently dimmed.
  busy = true;
  // Same `.bb-loading` dim markCartBusy uses elsewhere (see onRemoveClickCapture),
  // so a Remove click that lands on the cart page while THIS add-on bundle is in
  // flight — a quick-add from the cart page's own recommendation rail — has
  // somewhere to visibly land instead of being preventDefault'ed with nothing to
  // show for it.
  const busyTargets = markCartBusy();

  try {
    await addItems(items, component);
  } catch (error) {
    // Same reasoning as the rejected-payload branch in addItems: a throw here
    // (network drop, malformed JSON) left the shopper with a closed modal and no
    // signal at all. Anything that reaches this catch means the add did not
    // complete, so say so rather than failing silently.
    console.error('[bb-addons] add to cart failed', error);
    hideCartOverlay();
    showAddError(component, 'Sorry, we couldn’t add this to your cart. Please try again.');
  } finally {
    busy = false;
    pending = null;
    clearCartBusy(busyTargets);
  }
}

/**
 * POST the bundle and refresh the cart UI the same way the native form does: request the
 * cart sections and dispatch a CartAddEvent so the bubble + drawer re-render.
 * @param {Array<{id: string, quantity: number, properties?: Record<string,string>}>} items
 * @param {Element} component
 */
/**
 * Surface a failed add on the PDP form the shopper is looking at.
 *
 * WHY THIS EXISTS. /cart/add.js with an `items` array is ATOMIC: if one line is
 * rejected — a service variant that sold out between page render and the click —
 * Shopify rejects the whole payload and the TREE IS NOT ADDED EITHER. This used
 * to console.error and return. By then the modal had already closed, so the
 * shopper watched it vanish, assumed success, and found an unchanged cart at
 * checkout. Clicking again reproduced the same silence.
 *
 * The modal is gone by this point, so the message goes where the shopper's
 * attention returns: the buy-buttons error span, the same element and the same
 * 10s timeout product-form.js uses for a native rejected add.
 *
 * @param {Element|null|undefined} component - the product-form-component
 * @param {string} message - Shopify's own description of the rejection
 */
function showAddError(component, message) {
  const text = message || 'Sorry, this could not be added to your cart.';

  // CartErrorEvent first: it is the theme-wide signal, and anything else
  // listening (a drawer, an app block) gets told even if this PDP has no error
  // span — the gift-card form and cart-items component use the same event.
  component?.dispatchEvent(new CartErrorEvent({ error: text, code: 'INVALID' }));

  const errorEl = component?.querySelector('[ref="addToCartTextError"]');
  if (errorEl) {
    // Replace only the text, keeping the inline error icon that ships inside the
    // span. Clearing text nodes rather than innerHTML avoids destroying it.
    for (const node of [...errorEl.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    }
    errorEl.appendChild(document.createTextNode(text));
    errorEl.classList.remove('hidden');

    clearTimeout(errorTimers.get(component));
    errorTimers.set(
      component,
      setTimeout(() => {
        errorEl.classList.add('hidden');
        const live = component?.querySelector('[ref="liveRegion"]');
        if (live) live.textContent = '';
      }, ERROR_MESSAGE_DISPLAY_DURATION)
    );
  }

  // The error span is visual only; announce it too. aria-live="assertive" on
  // this node means a screen-reader user hears the failure instead of being left
  // with the same "nothing happened" the sighted shopper had.
  const live = component?.querySelector('[ref="liveRegion"]');
  if (live) live.textContent = text;
}

/**
 * Dispatch a CartLinesUpdateEvent and ALWAYS settle the deferred behind it.
 *
 * WHY THIS EXISTS. The event carries a promise that every listener awaits — the
 * cart icon, the cart-items component, the drawer. A deferred that is never
 * settled leaves all of them awaiting forever: the spinner spins, the cart UI
 * freezes, and it freezes on an operation that actually SUCCEEDED, because the
 * write already landed and only the follow-up read failed.
 *
 * Two ways that used to happen, both now closed:
 *   - fetchCart() catches its own errors and returns null on a network blip, and
 *     createCartFromAjaxResponse(null) throws.
 *   - a malformed/partial payload throws inside the same factory.
 * In both cases the throw happened AFTER dispatch, so listeners were already
 * attached to a promise nothing would ever settle.
 *
 * The payload is therefore built BEFORE the dispatch, so a failure has no
 * audience, and the resolve is still wrapped so nothing can escape unsettled.
 * When the payload cannot be built we dispatch anyway and REJECT: listeners have
 * an error path and can show it, which beats a stale cart that never resolves.
 *
 * @param {{action: string, context: string, lines: Array<{id: string, quantity: number}>, ajaxCart: unknown, detail: Record<string, unknown>}} args
 */
function dispatchCartUpdate({ action, context, lines, ajaxCart, detail }) {
  let payload = null;
  try {
    // Must be built by the library factory. Resolving with a raw /cart.js
    // payload makes downstream consumers throw on a missing property, which
    // rejects the promise for EVERY awaiter of this event — the cart icon and
    // cart-items component included, not just our own listeners.
    payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart), detail };
  } catch (error) {
    console.error('[bb-addons] could not build the cart payload after a successful write', error);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));

  if (!payload) {
    deferred.reject(new Error('bb-addons: the cart could not be re-read after a successful write'));
    return;
  }
  try {
    deferred.resolve(payload);
  } catch (error) {
    deferred.reject(error);
  }
}

async function addItems(items, component) {
  // Global loading state on the add button while the bundle POSTs (bb-design.css
  // styles [aria-busy="true"]). Cleared in finally for both success and error. [loading]
  const addButton = component?.querySelector(ADD_BUTTON_SELECTOR);
  addButton?.setAttribute('aria-busy', 'true');

  try {
    const body = /** @type {Record<string, unknown>} */ ({ items });
    const sections = cartSectionIds();
    if (sections) {
      body.sections = sections;
      // Preserve the query string (e.g. ?page=N on a paginated cart), matching
      // quick-order-list.js's own sections_url — only meaningful alongside a
      // sections request, which is why this lives inside the guard.
      body.sections_url = window.location.pathname + window.location.search;
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.status) {
      console.error('[bb-addons] cart rejected the add:', result.message, result.description);
      // `description` over `message`: Shopify sends message:"Cart Error" — a
      // generic label — and puts the sentence a shopper can act on in
      // `description` ("… is no longer available"). product-form.js renders
      // `message`, which is exactly the "told nothing" outcome this fixes, so
      // this path deliberately prefers the useful string and falls back.
      hideCartOverlay();
      showAddError(component, result.description || result.message);
      return;
    }

    const cart = await fetchCart();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    dispatchCartUpdate({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ id: String(item.id), quantity: item.quantity })),
      ajaxCart: cart,
      detail: {
        source: 'bb-addons',
        itemCount,
        productId: /** @type {HTMLElement} */ (component).dataset.productId,
        sections: result.sections,
        didError: false,
      },
    });
  } finally {
    addButton?.removeAttribute('aria-busy');
  }
}

// ── Cart grouping reconciliation (AC8) ───────────────────────────────────────

/**
 * Reconcile the cart after any change. Two passes, applied in one /cart/update.js call:
 *   1. Orphan removal (§9.4 / AC8): any add-on whose group has no surviving `parent` line
 *      is removed (e.g. the parent was deleted).
 *   2. Quantity sync (D5): any add-on whose quantity drifted from its parent's is snapped
 *      back, so editing a tree/plant's quantity on the cart page moves its add-ons with it.
 * @param {CartLinesUpdateEvent} event
 */
async function onCartUpdate(event) {
  if (reconciling) return;

  // Marked BEFORE awaiting the theme's promise, so our spinner is already up by
  // the time component-cart-items.js removes its own — the two overlap and the
  // shopper sees one continuous spinner instead of a gap. No-ops off the cart
  // page, where there is no cart-items-component to mark.
  const busyTargets = markCartBusy();
  try {
    await reconcileCart(event);
  } finally {
    // Let the re-render paint before dropping the spinner. Without this the
    // class is removed in the same task that dispatches the update, so the old
    // quantities are uncovered for a frame — the exact flash we are removing.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    clearCartBusy(busyTargets);
  }
}

/**
 * The reconcile itself. Split out so onCartUpdate can wrap every exit path —
 * including the early returns — in one busy-state finally.
 * @param {CartLinesUpdateEvent} event
 */
async function reconcileCart(event) {
  // v4 delivers the result asynchronously via event.promise rather than putting the
  // cart on the event itself. Awaiting it also means we reconcile AFTER the update
  // has actually landed, instead of racing it.
  let detail = null;
  let cart = null;
  try {
    const resolved = await event.promise;
    detail = resolved?.detail ?? null;
    cart = resolved?.cart ?? null;
  } catch {
    return; // the update failed; nothing to reconcile
  }
  if (detail?.source === 'bb-addons-reconcile') return;
  if (!cart || !Array.isArray(cart.items)) cart = await fetchCart();
  if (!cart) return;

  const orphanKeys = findOrphanKeys(cart);
  const qtyUpdates = findQuantityUpdates(cart, orphanKeys);
  if (!orphanKeys.length && !qtyUpdates.length) return;

  reconciling = true;
  try {
    // One request for both passes: orphans → 0, add-ons → their parent's quantity.
    const updates = /** @type {Record<string, number>} */ (Object.fromEntries(orphanKeys.map((k) => [k, 0])));
    for (const [key, qty] of qtyUpdates) updates[key] = qty;
    const body = /** @type {Record<string, unknown>} */ ({ updates });
    const sections = cartSectionIds();
    if (sections) {
      body.sections = sections;
      // Preserve the query string (e.g. ?page=N on a paginated cart), matching
      // quick-order-list.js's own sections_url — only meaningful alongside a
      // sections request, which is why this lives inside the guard.
      body.sections_url = window.location.pathname + window.location.search;
    }

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const updated = await response.json();
    if (updated.status) {
      // Same unchecked-status gap as removeGroup: a 4xx here is a RESOLVED fetch
      // with valid JSON, and dispatchCartUpdate's own swallowed build failure
      // meant this path had no way to notice one either. Nothing was optimistically
      // changed on screen for a reconcile, so there is no row to restore — but the
      // orphan/quantity drift this pass exists to fix is left standing, silently.
      throw new Error(updated.description || updated.message || 'the cart rejected the reconcile');
    }
    dispatchCartUpdate({
      action: 'update',
      context: 'cart',
      lines: [],
      ajaxCart: updated,
      detail: {
        source: 'bb-addons-reconcile',
        itemCount: updated.item_count,
        sections: updated.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[bb-addons] reconcile failed', error);
    // Same theme-wide signal component-cart-items.js dispatches on a failed
    // cart write — no product-form-component is in scope here, so this goes
    // straight to document, which is where every StandardEvents.cartError
    // listener already listens.
    document.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Sorry, we couldn’t update your cart. Please refresh and try again.',
        code: 'INVALID',
      })
    );
  } finally {
    reconciling = false;
  }
}

/**
 * Removing a parent on the cart page must take its add-ons with it INSTANTLY. The theme removes
 * one line per request, so letting it remove the parent and then reacting via reconcile means two
 * sequential round-trips (the add-ons visibly linger for seconds). Instead we intercept the
 * parent's remove click and delete the whole group in ONE /cart/update.js call.
 * @param {Event} event
 */
function onRemoveClickCapture(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('.cart-items__remove');
  if (!button) return;
  const row = button.closest('tr');
  // Only a group PARENT cascades; removing a single add-on is left to the theme.
  if (!row || !row.classList.contains('bb-addon-parent')) return;
  const group = /** @type {HTMLElement} */ (row).dataset.bbAddonGroup;
  if (!group) return;

  // Swallow, don't hand off, while an add or a reconcile pass is in flight.
  //
  // An earlier version bailed BEFORE preventDefault here, on the reasoning that
  // removeGroup() opens with this same guard, so cancelling the event first and
  // discovering that second produced a dead button was worse than handing the
  // click to the theme's own remove handler. That reasoning missed the case
  // that actually matters: the theme addresses lines by INDEX baked into the
  // markup at render time (cart-products.liquid → the theme's remove handler
  // → POST /cart/change.js), and a double-click landing while THIS group's own
  // removeGroup() request is in flight — rows already mid `.removing` — falls
  // through to a stale index. Because removeGroup()'s /cart/update.js call is
  // about to change how many lines exist, that stale-index POST can delete an
  // unrelated cart line. Swallowing the duplicate is the only click-during-
  // busy outcome that is always correct.
  //
  // Both branches this guards against mark `.cart-items` with the same
  // `.bb-loading` dim: removeGroup()/onCartUpdate() for `reconciling`, and
  // finalize() for `busy` (an add-on bundle in flight). Either way the click
  // has somewhere to visibly land, so swallowing it here does not read as a
  // dead button.
  if (busy || reconciling) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  removeGroup(group);
}

/** Remove every line sharing the group id in one request, with the theme's remove animation. */
async function removeGroup(group) {
  if (busy || reconciling) return;
  // `group` comes from a line-item property, which is visitor-controlled. Unescaped it can
  // throw a SyntaxError (silently killing the Remove button, since the caller has already
  // called preventDefault) or widen the selector to match rows it shouldn't zero out.
  const rows = [...document.querySelectorAll(`tr[data-bb-addon-group="${CSS.escape(group)}"]`)];
  const keys = rows.map((r) => /** @type {HTMLElement} */ (r).dataset.key).filter(Boolean);
  if (!keys.length) return;

  reconciling = true; // also stops onCartUpdate from double-acting on the resulting event
  // Same `.bb-loading` dim onCartUpdate uses elsewhere, so a click landing
  // anywhere in the cart while this request is in flight (including a
  // duplicate click on this very row — see onRemoveClickCapture) reads as
  // busy rather than dead.
  const busyTargets = markCartBusy();
  rows.forEach((r) => r.classList.add('removing'));
  try {
    const body = /** @type {Record<string, unknown>} */ ({
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    });
    const sections = cartSectionIds();
    if (sections) {
      body.sections = sections;
      // Preserve the query string (e.g. ?page=N on a paginated cart), matching
      // quick-order-list.js's own sections_url — only meaningful alongside a
      // sections request, which is why this lives inside the guard.
      body.sections_url = window.location.pathname + window.location.search;
    }

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const updated = await response.json();
    if (updated.status) {
      // A 4xx here is a RESOLVED fetch with valid JSON, same shape as a rejected
      // /cart/add.js (see addItems). Left unchecked, dispatchCartUpdate's own
      // swallowed throw (it only logs when it cannot build the payload) meant
      // nothing downstream ever threw either, so the catch below never ran and
      // `.removing` was never cleared: cart-products.liquid animates those rows
      // to height:0/opacity:0 with forwards, so they vanish while still in the
      // cart and the shopper pays for services they think they removed.
      throw new Error(updated.description || updated.message || 'the cart rejected the removal');
    }
    dispatchCartUpdate({
      action: 'update',
      context: 'cart',
      lines: [],
      ajaxCart: updated,
      detail: {
        source: 'bb-addons-reconcile',
        itemCount: updated.item_count,
        sections: updated.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[bb-addons] group remove failed', error);
    rows.forEach((r) => r.classList.remove('removing'));
    // Same theme-wide signal component-cart-items.js dispatches on a failed
    // cart write (see #handleCartError) — there is no product-form-component
    // in scope here to hand showAddError, so this goes straight to document,
    // which is where every existing StandardEvents.cartError listener already
    // listens.
    document.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Sorry, we couldn’t remove that from your cart. Please try again.',
        code: 'INVALID',
      })
    );
  } finally {
    reconciling = false;
    clearCartBusy(busyTargets);
  }
}

/**
 * Keep each add-on's quantity locked to its parent's (D5 — add-on qty = parent qty, 1:1).
 * When a tree/plant's quantity is edited on the cart page, its add-on lines must follow;
 * this returns the [key, quantity] pairs for any add-on whose quantity has drifted from
 * its parent's. The rule is parent-authoritative, so it also snaps a directly-edited add-on
 * back — the bundle can never desync. Orphans (being removed this pass) are skipped.
 * @param {{items: Array<{key: string, quantity: number, properties?: Record<string,string>}>}} cart
 * @param {string[]} orphanKeys
 * @returns {Array<[string, number]>}
 */
function findQuantityUpdates(cart, orphanKeys) {
  const orphaned = new Set(orphanKeys);
  const parentQty = new Map();
  for (const item of cart.items) {
    if (prop(item, '_addon_role') === 'parent') {
      const group = prop(item, '_addon_group');
      if (group) parentQty.set(group, item.quantity);
    }
  }
  const updates = [];
  for (const item of cart.items) {
    const role = prop(item, '_addon_role');
    const group = prop(item, '_addon_group');
    if (!role || role === 'parent' || !group || orphaned.has(item.key)) continue;
    const target = parentQty.get(group);
    if (typeof target === 'number' && target > 0 && item.quantity !== target) {
      updates.push([item.key, target]);
    }
  }
  return updates;
}

/** @param {{items: Array<{key: string, properties?: Record<string,string>}>}} cart */
function findOrphanKeys(cart) {
  const livingParents = new Set();
  for (const item of cart.items) {
    if (prop(item, '_addon_role') === 'parent') livingParents.add(prop(item, '_addon_group'));
  }
  const orphans = [];
  for (const item of cart.items) {
    const role = prop(item, '_addon_role');
    const group = prop(item, '_addon_group');
    if (role && role !== 'parent' && group && !livingParents.has(group)) orphans.push(item.key);
  }
  return orphans;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function prop(item, key) {
  return item.properties ? item.properties[key] : undefined;
}

function setText(scope, selector, value) {
  const el = scope.querySelector(selector);
  if (el) el.textContent = value;
}

/** Format an integer amount of cents as a localized currency string. */
function formatMoney(cents, currency) {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(2)}`;
  }
}

/**
 * Sum the selected add-ons × parent quantity and write it into the modal's total
 * row, so the customer sees the real add-on cost for a multi-quantity order.
 * @param {HTMLDialogElement} dialog
 */
function updateTotal(dialog) {
  const valueEl = dialog.querySelector('[data-bb-addons-total-value]');
  if (!valueEl) return;
  const quantity = (pending && pending.quantity) || 1;
  let cents = 0;
  for (const card of dialog.querySelectorAll('[data-bb-addon-card][aria-pressed="true"]')) {
    cents += (parseInt(/** @type {HTMLElement} */ (card).dataset.price || '0', 10) || 0) * quantity;
  }
  valueEl.textContent = formatMoney(cents, dialog.dataset.bbCurrency);
}

function cartSectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  return ids.length ? ids.join(',') : '';
}

async function fetchCart() {
  try {
    return await (await fetch(`${Theme.routes.cart_url}.js`)).json();
  } catch (error) {
    console.error('[bb-addons] could not read cart', error);
    return null;
  }
}

// ── Boot ─────────────────────────────────────────────────────────────────────

function boot() {
  if (typeof window === 'undefined') return;

  // Capture on window → runs before the theme's document-level capture delegation.
  window.addEventListener('click', onClickCapture, true);
  window.addEventListener('click', onRemoveClickCapture, true);
  window.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);

  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  if (dialog) {
    dialog.addEventListener('click', (event) => onDialogClick(dialog, event));
    // Escape / backdrop close still adds the plant (D1).
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('bb-modal-open'); // release scroll-lock [R-10]
      if (pending && !pending.finalized) finalize([]);
    });
  }
}

boot();
